import { z } from 'zod';
import { EntitySuggestionStatusSchema } from '../../entities/entity-suggestion';
import { OrgFunctionKindSchema } from '../../entities/org-function/org-function-ref.schema';
import { AddressSchema, PrivacyModeSchema } from '../../entities/organization/organization.schema';
import { ProcessCategorySchema, ProcessStatusSchema } from '../../entities/process/process.schema';
import { ChainSwimlaneSchema } from '../../entities/process-chain/process-chain.schema';
import { EventKindSchema } from '../../entities/process-node';
import { NoteCategorySchema } from '../../entities/process-note';
import { NoteTargetsSchema } from '../../entities/process-note/note-targets.schema';
import { ManualCapacityDisplaySchema, StepDecisionRuleInputSchema, StepVariantCheckSchema } from '../../entities/process-step-recording/process-step-recording.schema';
import { UserStatusSchema, UserTypeSchema } from '../../entities/user/user.schema';
import { CapacityPrioritySchema } from '../../shared/capacity-priority';
import { SupportedLanguageSchema } from '../../shared/language.schema';
import { StepActionTypeSchema } from '../../shared/step-action-type.schema';
import { SurveyTogglesSchema } from '../../shared/survey-toggles';
/**
 * Organization data export (`GET /api/v2/data-export/zip`): one schema per
 * file of the zip. Every object is strict and every leaf a closed type, so a
 * field the export builders did not name fails the parse instead of leaving
 * the server (guarded by `test/org-export-schema.test.ts`).
 */ // ---------- References ----------
export const OrgExportRefTypeSchema = z.enum([
    'person',
    'function',
    'system',
    'externalContact'
]);
export const OrgExportRefSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    removed: z.literal(true).optional()
});
export const OrgExportEntityRefSchema = z.strictObject({
    type: OrgExportRefTypeSchema,
    ...OrgExportRefSchema.shape
});
export const OrgExportSuggestionRefSchema = z.strictObject({
    type: OrgExportRefTypeSchema,
    name: z.string(),
    suggestion: EntitySuggestionStatusSchema.extract([
        'pending',
        'rejected'
    ])
});
export const OrgExportTypedRefSchema = z.union([
    OrgExportEntityRefSchema,
    OrgExportSuggestionRefSchema
]);
// ---------- Organization and lists ----------
export const OrgExportOrganizationSchema = z.strictObject({
    name: z.string(),
    domain: z.string().optional(),
    address: AddressSchema.strict().optional(),
    exportedAt: z.iso.datetime(),
    settings: z.strictObject({
        defaultLanguage: SupportedLanguageSchema,
        privacyMode: PrivacyModeSchema,
        reviewTimeframeDays: z.number(),
        standardRecordingTimeHours: z.number(),
        internalBillingRatePerHour: z.number().optional(),
        capacityPriority: CapacityPrioritySchema,
        surveys: SurveyTogglesSchema.omit({
            classic: true
        }).strict(),
        ai: z.strictObject({
            bliksy: z.boolean(),
            stepNameGeneration: z.boolean(),
            /** The AI features of each app (`settings.ai.apps.<app>.<feature>`) and whether each is on. */ appFeatures: z.array(z.strictObject({
                app: z.string(),
                feature: z.string(),
                enabled: z.boolean()
            }))
        })
    })
});
export const OrgExportMemberSchema = z.strictObject({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    department: OrgExportRefSchema.nullable(),
    mainFunction: OrgExportRefSchema.nullable(),
    otherFunctions: z.array(OrgExportRefSchema),
    location: z.string().optional(),
    roles: z.array(UserTypeSchema),
    status: UserStatusSchema,
    internal: z.boolean(),
    externalOrganization: z.string().optional(),
    jobTitle: z.string().optional()
});
export const OrgExportDepartmentSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    abbreviation: z.string().optional(),
    description: z.string().optional(),
    parent: OrgExportRefSchema.nullable(),
    manager: OrgExportRefSchema.nullable()
});
export const OrgExportFunctionSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    kind: OrgFunctionKindSchema,
    description: z.string().optional(),
    email: z.string().optional(),
    lead: OrgExportRefSchema.nullable(),
    department: OrgExportRefSchema.nullable()
});
export const OrgExportSystemSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    category: z.string().optional(),
    url: z.string().optional(),
    licenceSeats: z.number().optional(),
    owner: OrgExportRefSchema.nullable()
});
export const OrgExportExternalContactSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    email: z.string().optional(),
    type: z.string().optional(),
    description: z.string().optional()
});
export const OrgExportDocumentSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    type: z.string().optional(),
    description: z.string().optional(),
    link: z.string().optional()
});
export const OrgExportProcessChainSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    category: ProcessCategorySchema,
    e2eLabel: z.string().optional(),
    typicalInputs: z.array(z.string()).optional(),
    departmentName: z.string().optional(),
    parent: OrgExportRefSchema.nullable(),
    processes: z.array(z.strictObject({
        process: OrgExportRefSchema,
        lane: ChainSwimlaneSchema,
        order: z.number()
    }))
});
// ---------- Step building blocks ----------
const OrgExportEndSchema = z.enum([
    'none',
    'unclear'
]);
export const OrgExportEventSchema = z.strictObject({
    id: z.string(),
    kind: EventKindSchema,
    type: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional()
});
export const OrgExportTemplateEventSchema = OrgExportEventSchema.omit({
    kind: true
});
export const OrgExportInputSchema = z.strictObject({
    name: z.string().optional(),
    description: z.string().optional(),
    kind: z.enum([
        'previousOutput',
        'event'
    ]),
    from: z.array(z.strictObject({
        node: z.string(),
        rules: z.array(z.number()).optional()
    }))
});
export const OrgExportRuleSchema = z.strictObject({
    number: z.number(),
    conditions: z.array(StepDecisionRuleInputSchema.strict()),
    output: z.string().optional(),
    handsOverTo: z.array(OrgExportTypedRefSchema),
    end: OrgExportEndSchema.optional()
});
export const OrgExportCoPerformerSchema = z.union([
    OrgExportEntityRefSchema.extend({
        confirmed: z.boolean()
    }),
    OrgExportSuggestionRefSchema.extend({
        confirmed: z.boolean()
    })
]);
/**
 * `taskDescription`, `output`, `handsOverTo` and `end` are set on tasks only,
 * `rules` on decisions only.
 */ export const OrgExportStepContentSchema = z.strictObject({
    id: z.string(),
    type: StepActionTypeSchema,
    name: z.string(),
    description: z.string().optional(),
    performedBy: OrgExportTypedRefSchema.nullable(),
    alsoPerformedBy: z.array(OrgExportCoPerformerSchema),
    inputs: z.array(OrgExportInputSchema),
    helpers: z.strictObject({
        persons: z.array(OrgExportRefSchema),
        functions: z.array(OrgExportTypedRefSchema),
        systems: z.array(OrgExportTypedRefSchema),
        documents: z.array(OrgExportRefSchema)
    }),
    taskDescription: z.string().optional(),
    output: z.string().optional(),
    handsOverTo: z.array(OrgExportTypedRefSchema).optional(),
    end: OrgExportEndSchema.optional(),
    rules: z.array(OrgExportRuleSchema).optional()
});
export const OrgExportSurveyAnswerSchema = z.strictObject({
    rating: z.number().optional(),
    ratingComment: z.string().optional(),
    frequency: z.string().optional(),
    frequencyTimesPerPeriod: z.number().optional(),
    frequencyComment: z.string().optional(),
    durationMinutes: z.number().optional(),
    durationComment: z.string().optional()
});
export const OrgExportVariantCheckSchema = StepVariantCheckSchema.omit({
    variants: true
}).strict();
export const OrgExportManualCapacitySchema = ManualCapacityDisplaySchema.strict();
export const OrgExportPublishedStepSchema = OrgExportStepContentSchema.extend({
    number: z.number(),
    recorded: z.boolean(),
    recordedBy: OrgExportRefSchema.nullable(),
    surveyAnswers: z.array(OrgExportSurveyAnswerSchema).optional(),
    variantCheck: OrgExportVariantCheckSchema.optional(),
    manualCapacity: OrgExportManualCapacitySchema.optional()
});
export const OrgExportDraftStepSchema = OrgExportStepContentSchema.extend({
    number: z.number(),
    manualCapacity: OrgExportManualCapacitySchema.optional()
});
const OrgExportOpenStepStatusSchema = z.enum([
    'invited',
    'accepted',
    'assigneeUnclear'
]);
export const OrgExportOpenStepSchema = z.strictObject({
    id: z.string(),
    assignedTo: OrgExportTypedRefSchema.nullable(),
    status: OrgExportOpenStepStatusSchema,
    inputs: z.array(OrgExportInputSchema)
});
/** Draft assignments store no step type, performer or task/decision content. */ export const OrgExportDraftOpenStepSchema = OrgExportStepContentSchema.pick({
    id: true,
    name: true,
    description: true,
    alsoPerformedBy: true,
    inputs: true,
    helpers: true
}).extend({
    assignedTo: OrgExportTypedRefSchema.nullable(),
    status: OrgExportOpenStepStatusSchema.optional()
});
export const OrgExportNoteSchema = z.strictObject({
    text: z.string(),
    category: NoteCategorySchema.optional(),
    targets: NoteTargetsSchema
});
export const OrgExportLinkedProcessSchema = z.strictObject({
    fromStep: z.string(),
    rule: z.number().optional(),
    process: OrgExportRefSchema,
    toEvent: z.strictObject({
        id: z.string(),
        name: z.string()
    }).optional()
});
export const OrgExportDraftSchema = z.strictObject({
    events: z.array(OrgExportEventSchema),
    steps: z.array(OrgExportDraftStepSchema),
    openSteps: z.array(OrgExportDraftOpenStepSchema),
    notes: z.array(OrgExportNoteSchema)
});
// ---------- Process and step template files ----------
export const OrgExportProcessSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    category: ProcessCategorySchema.optional(),
    apqcId: z.string().optional(),
    status: ProcessStatusSchema,
    department: OrgExportRefSchema.nullable(),
    owner: OrgExportRefSchema.nullable(),
    accountable: OrgExportRefSchema.nullable(),
    recording: z.strictObject({
        startedAt: z.iso.datetime().nullable(),
        endedAt: z.iso.datetime().nullable()
    }),
    events: z.array(OrgExportEventSchema),
    steps: z.array(OrgExportPublishedStepSchema),
    openSteps: z.array(OrgExportOpenStepSchema),
    notes: z.array(OrgExportNoteSchema),
    linkedProcesses: z.array(OrgExportLinkedProcessSchema),
    draft: OrgExportDraftSchema.optional()
});
export const OrgExportStepTemplateSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    events: z.array(OrgExportTemplateEventSchema),
    steps: z.array(OrgExportStepContentSchema)
});
// ---------- List files ----------
export const OrgExportMembersSchema = z.array(OrgExportMemberSchema);
export const OrgExportDepartmentsSchema = z.array(OrgExportDepartmentSchema);
export const OrgExportFunctionsSchema = z.array(OrgExportFunctionSchema);
export const OrgExportSystemsSchema = z.array(OrgExportSystemSchema);
export const OrgExportExternalContactsSchema = z.array(OrgExportExternalContactSchema);
export const OrgExportDocumentsSchema = z.array(OrgExportDocumentSchema);
export const OrgExportProcessChainsSchema = z.array(OrgExportProcessChainSchema);
export const OrgExportStepTemplatesSchema = z.array(OrgExportStepTemplateSchema);
/** The fixed files of the zip; every `processes/*.json` is an `OrgExportProcessSchema`. */ export const ORG_EXPORT_FILE_SCHEMAS = {
    'organization.json': OrgExportOrganizationSchema,
    'members.json': OrgExportMembersSchema,
    'departments.json': OrgExportDepartmentsSchema,
    'functions.json': OrgExportFunctionsSchema,
    'systems.json': OrgExportSystemsSchema,
    'external-contacts.json': OrgExportExternalContactsSchema,
    'documents.json': OrgExportDocumentsSchema,
    'process-chains.json': OrgExportProcessChainsSchema,
    'step-templates.json': OrgExportStepTemplatesSchema
};

//# sourceMappingURL=org-export.schema.js.map